import { head, last, merge } from 'lodash';
import moment from 'moment';

class AvailabilitySliderController {

    constructor(availabilityService, availabilityApiService, dateService, itemService, storeService) {
        this.availabilityService = availabilityService;
        this.availabilityApiService = availabilityApiService;
        this.dateService = dateService;
        this.itemService = itemService;
        this.storeService = storeService;
    }

    $onInit () {
        this.travelNext = null;
        this.travelPrevious = null;
        this.loading = true;
        this.today = moment();
        this.startDate = null;
        this.endDate = null;
        this.error = false;
        this.isSearching = false;

        this.setOptions();

        this.item = this.itemService.getItem();

        this.availabilityService.resetSingleDays();
    }

    setupDays () {
        if (!this.loading && this.availabilityService.hasAvailability()) {
            if (!this.availabilityService.hasSingleDays()) {
                this.setDays();
            }

            return this.availabilityService.getSingleDays();
        }
    }

    setDays () {
        this.availabilityService.resetSingleDays();
        let days = [];

        this.availabilityService.getAvailability().forEach((availability) => {
            let day = moment(availability.date);

            days.push(merge({
                name: day.format('ddd'),
                number: day.format('DD')
            }, availability));
        });

        this.availabilityService.setSingleDays(days);

        return this.itemService.setupItemAvailability(this.availabilityService.getAvailability(), this.startDate).then(response => {
            this.getQuantity();
        });
    }

    monthYear () {
        if (this.availabilityService.hasAvailability()) {
            const availability = this.availabilityService.getAvailability();
            this.startDate = moment(head(availability).date, 'YYYY-MM-DD');
            this.endDate = moment(last(availability).date, 'YYYY-MM-DD');

            if (this.startDate.isSame(this.endDate, 'month')) {
                return this.startDate.format('MMMM YYYY');
            }

            if (this.startDate.isSame(this.endDate, 'year')) {
                return `${this.startDate.format('MMM')}–${this.endDate.format('MMM YYYY')}`;
            }

            return `${this.startDate.format('MMM YYYY')}–${this.endDate.format('MMM YYYY')}`;
        }

        let searchDate = this.storeService.get('searchDate');
        if (searchDate) {
            return moment(searchDate).format('MMMM YYYY');
        }

        return this.today.format('MMMM YYYY');
    }

    selectDay (availableDay) {
        if (!this.isDateSelected(availableDay)) {
            this.staff = undefined;

            return this.itemService.updateAvailability(availableDay).then(() => {
                this.item = this.itemService.getItem();
                this.item.interacted = true;
                this.itemService.setItem(this.item);

                this.getQuantity();
            });
        }
    }

    isDateSelected (day) {
        if (this.itemService.hasItem()) {
            if (!this.item) {
                this.item = this.itemService.getItem();
            }
            return this.item.date === day.date;
        }
    }

    isNextDateValid (direction = null) {
        if (!this.loading && !this.error
            && this.availabilityService.hasAvailability()) {
            if (direction === 'previous') {
                return !this.startDate.isSame(this.today, 'day');
            }

            return !moment(this.endDate).add(1, 'days').diff(this.today, 'years') > 0;
        }

        return false;
    }

    nextAvailability () {
        this.travelNext = true;
        this.isSearching = true;

        return this.updateAvailability('next').then(() => {
            this.travelNext = null;
        });
    }

    previousAvailability () {
        this.travelPrevious = true;
        this.isSearching = true;

        return this.updateAvailability('previous').then(() => {
            this.travelPrevious = null;
        });
    }

    updateAvailability (direction = undefined) {
        this.loading = true;
        this.error = false;
        this.availabilityService.resetSingleDays();
        this.availabilityService.resetSingleTimes();

        this.setStartEndDates(direction);

        if (this.basket && this.basket.store_offer) {
            this.item.offer = {
                id: this.basket.store_offer
            };
            this.itemService.setItem(this.item);
        }

        const offer = this.item.offer ? this.item.offer.id : 'storefront';

        return this.availabilityApiService.requestAvailability(this.startDate.format('YYYY-MM-DD'), this.endDate.format('YYYY-MM-DD'), this.item.event_type || this.item.type_name, this.item.id, offer)
            .then(response => {
                this.availabilityService.setAvailability(response);

                return this.setupDays(false);
            })
            .catch(() => {
                this.error = true;
            })
            .then(() => {
                this.loading = false;
            });
    }

    setStartEndDates (direction) {
        let start = null;
        let end = null;

        switch (direction) {
            case 'next':
                start = moment(this.endDate.add(1, 'days'));
                end = this.endDate.add(6, 'days');
                break;
            case 'previous':
                end = moment(this.startDate.subtract(1, 'days'));
                start = this.startDate.subtract(6, 'days');
                break;
            default:
                start = moment(this.startDate);
                end = this.startDate.add(6, 'days');
                this.isSearching = false;
        }

        this.startDate = this.dateService.checkMaxDate(start, true);
        this.endDate = this.dateService.checkMaxDate(end);

        if (!this.dateService.verifyDateGteToday(this.startDate)) {
            this.startDate = moment();
            this.endDate = moment().add(6, 'days');
        }
        this.storeService.set('searchDate', this.startDate.format('YYYY-MM-DD'));
    }

    goToToday () {
        this.startDate = moment();
        return this.updateAvailability();
    }

    updateDate (date) {
        if (date && date[0] && !this.isDateSelected({ date: date[0] })) {
            this.staff = undefined;

            this.startDate = this.dateService.checkDate(date[0], true);
            this.item.date = this.startDate.format('YYYY-MM-DD');

            return this.updateAvailability();
        }
    }

    displayOriginalPrice (day) {
        return !!(day.available && day.original_price !== day.price);
    }

    hasLoaded () {
        return !!(!this.loading && this.availabilityService.hasAvailability());
    }

    setOptions () {
        let minDate = 'today';
        let maxDate = moment().add(1, 'year').format('YYYY-MM-DD');

        const startDate = this.storeService.get('start_date');
        if (startDate) {
            minDate = moment(startDate, 'YYYY-MM-DD').format('YYYY-MM-DD');
            maxDate = moment(this.storeService.get('end_date'), 'YYYY-MM-DD').format('YYYY-MM-DD');
        }

        this.flatpickrOptions = {
            placeholder: null,
            minDate: minDate,
            maxDate: maxDate,
            disableMobile: "true",
            onChange: (date) => {
                this.updateDate(date);
            }
        };
    }

    setFlatpickrInstance (fpItem) {
        this.flatpickr = fpItem;
    };
}

AvailabilitySliderController.$inject = ['availabilityService', 'availabilityApiService', 'dateService', 'itemService', 'storeService'];

export default AvailabilitySliderController;



// WEBPACK FOOTER //
// ./frontend/admin/app/modules/basket/controllers/availability.slider.controller.js